import { clsx } from 'clsx'
import { Moon, Sun } from 'lucide-react'
import { useTranslation } from '@/shared/i18n/useTranslation'
import { resolvedTheme, useAppStore } from '@/shared/stores/appStore'
import styles from './ThemeToggle.module.css'

/**
 * Light/dark as a single switch in the header.
 *
 * The icon shows the theme that is ON — a sun while the lights are on — and the
 * label says what pressing it will do, so the button is unambiguous either way
 * round.
 *
 * A two-state switch cannot express "follow the system", so pressing it always
 * commits to an explicit light or dark. Someone who wants to hand the choice
 * back to the OS can still pick System in Preferences; this resolves that case
 * against the OS first so the first press flips what the viewer actually sees,
 * rather than appearing to do nothing.
 */
export function ThemeToggle() {
  const { t } = useTranslation()
  const theme = useAppStore((s) => s.theme)
  const setTheme = useAppStore((s) => s.setTheme)

  const isLight = resolvedTheme(theme) === 'light'

  return (
    <button
      type="button"
      className={clsx(styles.toggle, isLight && styles.isLight)}
      onClick={() => setTheme(isLight ? 'dark' : 'light')}
      aria-label={isLight ? t('prefs.switchToDark') : t('prefs.switchToLight')}
      title={isLight ? t('prefs.switchToDark') : t('prefs.switchToLight')}
    >
      {isLight ? (
        <Sun size={20} className={styles.icon} aria-hidden="true" />
      ) : (
        <Moon size={20} className={styles.icon} aria-hidden="true" />
      )}
    </button>
  )
}
